/* Impression SCDM — DECOUPE A LA FORME (cut contour).

   LE PROBLEME. Dans scdm.js, la fonction chemin() ne connait que trois
   geometries : le rectangle, le rectangle a coins arrondis, et l ellipse. Or
   elle traite « a la forme » (forme = 'contour') EXACTEMENT comme « rond » :

       if (forme === 'rond' || forme === 'contour') { c.ellipse(...); }

   Autrement dit, choisir « A la forme » donnait un cercle. Sur un pictogramme
   SIMDUT — un losange rouge — le client voyait sa ligne de coupe passer en
   rond autour du losange. Ce n est pas une decoupe a la forme, c est un rond.

   CE QUE FAIT CE FICHIER. Il calcule la vraie silhouette du dessin place sur
   l apercu, la grossit de la largeur du contour blanc (le curseur CONTOUR),
   et impose ce trace a la place de l ellipse. Les trois usages de chemin()
   dans scdm.js — le fond du collant, le detourage de l image, le reflet du
   vernis — recoivent donc tous la bonne forme, et la ligne magenta de
   scdm-coupe.js, qui lit la silhouette de la toile, suit toute seule.

   COMMENT ON S ACCROCHE, ET POURQUOI COMME CA. chemin() est enfermee dans la
   fermeture de scdm.js : on ne peut pas la remplacer de l exterieur. Mais
   getContext('2d') rend TOUJOURS le meme objet pour une meme toile — celui-la
   meme que scdm.js utilise. On remplace donc la methode ellipse() de CET
   objet, et seulement quand la forme choisie est « contour ». Les trois
   autres formes, y compris le rond, ne sont jamais touchees.

   C est un branchement etroit, et il est volontaire : reecrire scdm.js coute
   39 Ko de reecriture a chaque virgule. Le jour ou ce fichier sera repris,
   ce code a sa place dans chemin(), juste avant le if. En attendant, la seule
   chose a ne pas casser est que chemin() reste le SEUL endroit de scdm.js qui
   appelle ellipse(). Si un jour il en existe un deuxieme, il faudra resserrer
   le test ci-dessous sur la geometrie passee.

   TROIS SORTES DE FICHIERS, TROIS FACONS DE TROUVER LA SILHOUETTE :

     1. FOND TRANSPARENT (PNG detoure, SVG, PDF sur fond vide) — le canal
        alpha donne la silhouette directement. C est le cas propre.
     2. FOND UNI (le logo enregistre en JPG sur du blanc) — on regarde les
        quatre bords de l image ; s ils sont d une seule couleur, on la traite
        comme du fond et on la retire. C est le cas le plus frequent en vrai.
     3. NI L UN NI L AUTRE (une photo) — il n y a rien a detourer, la decoupe
        suit le rectangle. On l ECRIT sous l apercu : un client qui croit
        commander une decoupe a la forme et recoit un carre, c est un retour.

   Ce fichier ne touche ni au prix, ni au fichier envoye, ni a la commande.
*/

(function () {
  'use strict';

  var R = 0.5;          /* on travaille le masque a demi-resolution : quatre fois
                           moins de pixels, aucune difference visible apres
                           lissage, et ca tient dans une image de 60 fps */
  var PAD = 26;         /* marge vide autour du masque (en pixels du masque) :
                           le suivi de bord a besoin que la forme ne touche
                           jamais le bord de la toile */
  var SEUIL = 128;      /* au-dela, le pixel appartient au dessin */
  var TOL_FOND = 42;    /* distance de couleur toleree autour du fond uni */
  var MINI = 60;        /* moins que ca de pixels, ce n est pas une forme */

  var cv = null, ctx = null, ellipseOrig = null;
  var analyse = null;   /* { mode, r, g, b } — refait a chaque nouvelle image */
  var cache = null;     /* { sig, rel, mode } */
  var note = null;
  var derniere = null;  /* la derniere image analysee */

  /* =====================================================================
     1. ANALYSE DE L IMAGE — une seule fois par fichier
     ===================================================================== */

  function analyser(img) {
    var m = Math.max(img.width, img.height);
    var e = Math.min(1, 256 / (m || 1));
    var w = Math.max(1, Math.round(img.width * e));
    var h = Math.max(1, Math.round(img.height * e));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var k = c.getContext('2d', { willReadFrequently: true });
    k.clearRect(0, 0, w, h);
    k.drawImage(img, 0, 0, w, h);

    var d;
    try { d = k.getImageData(0, 0, w, h).data; }
    catch (e2) { return { mode: 'plein' }; }

    var n = w * h, transparents = 0, i;
    for (i = 3; i < d.length; i += 4) if (d[i] <= SEUIL) transparents++;

    /* 1. vrai fond transparent */
    if (transparents > n * 0.02) return { mode: 'alpha' };

    /* 2. fond uni : on lit le cadre de 1 pixel et on demande qu il soit
          d une seule couleur. La mediane plutot que la moyenne, pour qu un
          coin de logo qui deborde ne deplace pas la couleur de reference. */
    var rs = [], gs = [], bs = [];
    function bord(x, y) {
      var p = (y * w + x) * 4;
      rs.push(d[p]); gs.push(d[p + 1]); bs.push(d[p + 2]);
    }
    for (var x = 0; x < w; x++) { bord(x, 0); bord(x, h - 1); }
    for (var y = 1; y < h - 1; y++) { bord(0, y); bord(w - 1, y); }

    function median(a) { a = a.slice().sort(function (p, q) { return p - q; }); return a[a.length >> 1]; }
    var fr = median(rs), fg = median(gs), fb = median(bs);

    var proches = 0;
    for (i = 0; i < rs.length; i++) {
      var dr = rs[i] - fr, dg = gs[i] - fg, db = bs[i] - fb;
      if (Math.sqrt(dr * dr + dg * dg + db * db) <= TOL_FOND) proches++;
    }
    if (proches < rs.length * 0.92) return { mode: 'plein' };

    /* Le fond doit retirer quelque chose, sans tout retirer : entre 2 et 96 %
       de l image. Sinon on a mal devine, et on prefere le rectangle. */
    var dedans = 0;
    for (i = 0; i < n; i++) {
      var q = i * 4;
      var er = d[q] - fr, eg = d[q + 1] - fg, eb = d[q + 2] - fb;
      if (Math.sqrt(er * er + eg * eg + eb * eb) > TOL_FOND) dedans++;
    }
    if (dedans < n * 0.02 || dedans > n * 0.96) return { mode: 'plein' };

    return { mode: 'fond', r: fr, g: fg, b: fb };
  }

  /* =====================================================================
     2. GEOMETRIE — recopiee de peindreApercu() dans scdm.js.
        Si cette fonction change la-bas, elle doit changer ici. C est le prix
        du branchement etroit, et c est ecrit noir sur blanc pour qu on le
        sache le jour ou ca decalera.
     ===================================================================== */

  function geometrie() {
    var S = window.SCDM, D = window.SCDM_DATA;
    if (!S || !D || !S.etat || !S.etat.img || !cv) return null;
    var e = S.etat, TC = cv.width;

    var surMesure = e.taille === 'custom';
    var marge = TC * 0.115, dispo = TC - marge * 2;
    var L = surMesure ? (Number(e.larg) || 1) : 1;
    var H = surMesure ? (Number(e.haut) || 1) : 1;
    var w = dispo, h = dispo;
    if (L > H) h = dispo * (H / L); else if (H > L) w = dispo * (L / H);
    var x = (TC - w) / 2, y = (TC - h) / 2;
    var b = Math.min(w, h) * e.bordure;
    var zw = w - b * 2, zh = h - b * 2;
    var im = e.img;
    var ech = Math.min(zw / im.width, zh / im.height) * e.zoom;
    var iw = im.width * ech, ih = im.height * ech;

    return {
      TC: TC, x: x, y: y, w: w, h: h, b: b, img: im,
      ix: x + b + (zw - iw) / 2 + e.dx,
      iy: y + b + (zh - ih) / 2 + e.dy,
      iw: iw, ih: ih
    };
  }

  /* =====================================================================
     3. LE MASQUE : silhouette pleine, sans trous, d un seul tenant
     ===================================================================== */

  /* Le masque epouse le DESSIN, pas la toile. Deux gains, et le second est le
     vrai : quatre fois moins de pixels a parcourir, et surtout le trace
     devient INDEPENDANT de la position. Deplacer le dessin a la souris ne
     change plus la silhouette, seulement son point d ancrage : on ne
     recalcule plus rien pendant un glissement.

     Aucune reference au configurateur ici : on donne une image, la taille a
     laquelle la dessiner, la marge, et la facon de lire le fond. C est ce qui
     permet au PDF de decoupe de refaire EXACTEMENT le meme trace, mais a la
     resolution d impression. */
  function masqueDe(img, dw, dh, P, ana) {
    var mw = Math.max(8, Math.round(dw)) + P * 2;
    var mh = Math.max(8, Math.round(dh)) + P * 2;

    var c = document.createElement('canvas');
    c.width = mw; c.height = mh;
    var k = c.getContext('2d', { willReadFrequently: true });
    k.drawImage(img, P, P, dw, dh);

    var d;
    try { d = k.getImageData(0, 0, mw, mh).data; }
    catch (e) { return null; }

    var N = mw * mh, plein = new Uint8Array(N), total = 0, i, p;
    var mode = (ana && ana.mode) || 'plein';

    if (mode === 'fond') {
      var fr = ana.r, fg = ana.g, fb = ana.b, t2 = TOL_FOND * TOL_FOND;
      for (i = 0, p = 0; i < N; i++, p += 4) {
        if (d[p + 3] <= SEUIL) continue;
        var dr = d[p] - fr, dg = d[p + 1] - fg, db = d[p + 2] - fb;
        if (dr * dr + dg * dg + db * db > t2) { plein[i] = 1; total++; }
      }
    } else {
      for (i = 0, p = 3; i < N; i++, p += 4) {
        if (d[p] > SEUIL) { plein[i] = 1; total++; }
      }
    }
    if (total < MINI) return null;

    /* La plus grande piece d un seul tenant. Un dessin peut avoir des eclats
       detaches (un point sur un i, une poussiere de compression JPEG) ; on ne
       decoupe pas autour d une poussiere. */
    var etiq = new Int32Array(N), pile = new Int32Array(N);
    var num = 0, chef = 0, chefT = 0;
    for (var s = 0; s < N; s++) {
      if (!plein[s] || etiq[s]) continue;
      num++;
      var t = 0, sp = 0;
      pile[sp++] = s; etiq[s] = num;
      while (sp) {
        var q = pile[--sp]; t++;
        var qx = q % mw, qy = (q / mw) | 0;
        if (qx > 0 && plein[q - 1] && !etiq[q - 1]) { etiq[q - 1] = num; pile[sp++] = q - 1; }
        if (qx < mw - 1 && plein[q + 1] && !etiq[q + 1]) { etiq[q + 1] = num; pile[sp++] = q + 1; }
        if (qy > 0 && plein[q - mw] && !etiq[q - mw]) { etiq[q - mw] = num; pile[sp++] = q - mw; }
        if (qy < mh - 1 && plein[q + mw] && !etiq[q + mw]) { etiq[q + mw] = num; pile[sp++] = q + mw; }
      }
      if (t > chefT) { chefT = t; chef = num; }
    }
    if (chefT < MINI) return null;

    var forme = new Uint8Array(N);
    for (i = 0; i < N; i++) forme[i] = etiq[i] === chef ? 1 : 0;

    /* Boucher les trous : ce que le fond n atteint pas depuis les bords est a
       l interieur du collant. Sans ca, un logo en anneau serait decoupe deux
       fois — une ligne de coupe au milieu de l autocollant. */
    var fond = new Uint8Array(N), sp2 = 0;
    function amorcer(k2) { if (!forme[k2] && !fond[k2]) { fond[k2] = 1; pile[sp2++] = k2; } }
    for (var bx = 0; bx < mw; bx++) { amorcer(bx); amorcer((mh - 1) * mw + bx); }
    for (var by = 0; by < mh; by++) { amorcer(by * mw); amorcer(by * mw + mw - 1); }
    while (sp2) {
      var u = pile[--sp2], ux = u % mw, uy = (u / mw) | 0;
      if (ux > 0 && !forme[u - 1] && !fond[u - 1]) { fond[u - 1] = 1; pile[sp2++] = u - 1; }
      if (ux < mw - 1 && !forme[u + 1] && !fond[u + 1]) { fond[u + 1] = 1; pile[sp2++] = u + 1; }
      if (uy > 0 && !forme[u - mw] && !fond[u - mw]) { fond[u - mw] = 1; pile[sp2++] = u - mw; }
      if (uy < mh - 1 && !forme[u + mw] && !fond[u + mw]) { fond[u + mw] = 1; pile[sp2++] = u + mw; }
    }
    for (i = 0; i < N; i++) if (!fond[i]) forme[i] = 1;

    return { m: forme, mw: mw, mh: mh, P: P };
  }

  /* =====================================================================
     4. GROSSIR DE LA LARGEUR DU CONTOUR
        La ligne de coupe ne passe pas SUR le dessin : elle passe au large,
        de la largeur du liston blanc. Mathematiquement c est une dilatation
        par un disque. On la fait en tamponnant la silhouette tout autour
        d un cercle : c est exact a la resolution pres, et c est du dessin de
        toile, donc de la carte graphique.
     ===================================================================== */

  function grossir(mask, rayon) {
    var mw = mask.mw, mh = mask.mh, N = mw * mh;
    if (rayon < 1) return mask;

    var a = document.createElement('canvas');
    a.width = mw; a.height = mh;
    var ka = a.getContext('2d');
    var img = ka.createImageData(mw, mh), dd = img.data;
    for (var i = 0, p = 3; i < N; i++, p += 4) if (mask.m[i]) dd[p] = 255;
    ka.putImageData(img, 0, 0);

    var bcv = document.createElement('canvas');
    bcv.width = mw; bcv.height = mh;
    var kb = bcv.getContext('2d', { willReadFrequently: true });
    var tours = Math.max(16, Math.min(64, Math.round(rayon * 3)));
    for (var t = 0; t < tours; t++) {
      var ang = 2 * Math.PI * t / tours;
      kb.drawImage(a, Math.cos(ang) * rayon, Math.sin(ang) * rayon);
    }
    kb.drawImage(a, 0, 0);

    var d2 = kb.getImageData(0, 0, mw, mh).data;
    var gros = new Uint8Array(N);
    for (var j = 0, q = 3; j < N; j++, q += 4) gros[j] = d2[q] > 8 ? 1 : 0;
    return { m: gros, mw: mw, mh: mh, P: mask.P };
  }

  /* =====================================================================
     5. DU MASQUE AU TRACE
     ===================================================================== */

  /* Suivi de bord de Moore : on tourne autour de la forme en gardant le plein
     du meme cote, et on note les points dans l ordre. Il FAUT l ordre : des
     pixels de bord en vrac ne donnent pas un chemin, donc pas de pointille
     regulier, et rien d exportable en PDF. */
  function suivre(m, mw, mh, sx, sy) {
    var V = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    function plein(x, y) { return x >= 0 && y >= 0 && x < mw && y < mh && m[y * mw + x] === 1; }
    var pts = [], cx = sx, cy = sy, dir = 6, garde = 0, maxi = 16 * (mw + mh);
    do {
      pts.push([cx, cy]);
      var bouge = false;
      for (var k = 0; k < 8; k++) {
        var nd = (dir + 1 + k) % 8;
        var nx = cx + V[nd][0], ny = cy + V[nd][1];
        if (plein(nx, ny)) { cx = nx; cy = ny; dir = (nd + 5) % 8; bouge = true; break; }
      }
      if (!bouge) break;
      garde++;
    } while (!(cx === sx && cy === sy) && garde < maxi);
    return pts;
  }

  /* Moyenne glissante refermee sur elle-meme : enleve l escalier des pixels
     sans deplacer la ligne. */
  function lisser(pts, fen) {
    var n = pts.length;
    if (n < fen * 2) return pts;
    var out = new Array(n), demi = fen >> 1;
    for (var i = 0; i < n; i++) {
      var sx = 0, sy = 0;
      for (var k = -demi; k <= demi; k++) {
        var p = pts[(i + k + n) % n];
        sx += p[0]; sy += p[1];
      }
      out[i] = [sx / fen, sy / fen];
    }
    return out;
  }

  /* Ramer-Douglas-Peucker : on jette les points qui ne changent rien a la
     ligne. On passe de ~3000 points a ~200, ce qui allege autant l affichage
     que le PDF de decoupe. */
  function alleger(pts, eps) {
    if (pts.length < 3) return pts;
    var garde = new Uint8Array(pts.length);
    garde[0] = 1; garde[pts.length - 1] = 1;
    var pile = [[0, pts.length - 1]];
    while (pile.length) {
      var seg = pile.pop(), a = seg[0], b = seg[1];
      if (b <= a + 1) continue;
      var ax = pts[a][0], ay = pts[a][1], bx = pts[b][0], by = pts[b][1];
      var vx = bx - ax, vy = by - ay, len = Math.sqrt(vx * vx + vy * vy) || 1;
      var pire = 0, iPire = -1;
      for (var i = a + 1; i < b; i++) {
        var d = Math.abs((pts[i][0] - ax) * vy - (pts[i][1] - ay) * vx) / len;
        if (d > pire) { pire = d; iPire = i; }
      }
      if (pire > eps && iPire > 0) {
        garde[iPire] = 1;
        pile.push([a, iPire]); pile.push([iPire, b]);
      }
    }
    var out = [];
    for (var j = 0; j < pts.length; j++) if (garde[j]) out.push(pts[j]);
    return out;
  }

  /* =====================================================================
     6. LE TRACE COMPLET, MIS EN CACHE
     ===================================================================== */

  function signature(g) {
    var e = window.SCDM.etat;
    /* NI ix NI iy : le trace est garde en coordonnees relatives au dessin,
       donc un simple deplacement n invalide rien. */
    return [g.img.src.length, g.img.width, g.img.height,
            Math.round(g.iw), Math.round(g.ih),
            Math.round(g.b * 10), e.taille, e.larg, e.haut,
            (analyse && analyse.mode) || '-'].join('|');
  }

  /* Trace complet pour une image, a la resolution qu on veut.
     dw / dh : taille du dessin en pixels de travail.
     rayon   : le liston blanc, dans la meme unite.
     Rend les points en pixels de travail, origine au coin haut-gauche du
     DESSIN. Null si la silhouette n a pas de sens. */
  function tracerDe(img, dw, dh, rayon, ana, eps) {
    var P = Math.ceil(Math.max(0, rayon)) + 10;
    var m = masqueDe(img, dw, dh, P, ana);
    if (!m) return null;

    m = grossir(m, rayon);

    var depart = -1, N = m.mw * m.mh;
    for (var i = 0; i < N && depart < 0; i++) if (m.m[i]) depart = i;
    if (depart < 0) return null;

    var pts = suivre(m.m, m.mw, m.mh, depart % m.mw, (depart / m.mw) | 0);
    if (pts.length < 12) return null;

    pts = lisser(pts, 7);
    pts = alleger(pts, eps || 0.7);
    if (pts.length < 6) return null;

    return pts.map(function (q) { return [q[0] - P, q[1] - P]; });
  }

  function calculer() {
    var g = geometrie();
    if (!g) { window.SCDM_FORME = null; return null; }

    /* L analyse du fichier doit etre a jour AVANT de tracer. La veille de la
       section 9 ne passe que quatre fois par seconde : sans ce controle ici,
       le premier rendu d un nouveau dessin etait detoure avec le reglage du
       dessin PRECEDENT, et on voyait la forme sauter un quart de seconde plus
       tard. On rattrape donc au moment ou on en a besoin. */
    if (g.img !== derniere) {
      derniere = g.img;
      cache = null;
      analyse = analyser(g.img);
      rafraichirNote();
    }

    var sig = signature(g);

    /* Deja calcule : il ne reste qu a le poser la ou le dessin se trouve
       maintenant. C est ce qui rend le glissement a la souris fluide. */
    if (cache && cache.sig === sig) return publier(cache, g);

    var pts = tracerDe(g.img, g.iw * R, g.ih * R, g.b * R, analyse, 0.7);
    if (!pts) { cache = null; window.SCDM_FORME = null; return null; }

    /* Coordonnees RELATIVES au coin haut-gauche du dessin place, en pixels de
       la toile du configurateur. On y ajoute la position au moment de s en
       servir : voir publier(). */
    cache = {
      sig: sig,
      rel: pts.map(function (q) { return [q[0] / R, q[1] / R]; }),
      mode: (analyse && analyse.mode) || 'plein'
    };
    return publier(cache, g);
  }

  /* Pose le trace a la position courante et le publie. window.SCDM_FORME sert
     au PDF de decoupe : il doit TOUJOURS refleter ce qui est a l ecran, y
     compris quand le trace vient du cache. */
  function publier(c, g) {
    c.geo = g;
    c.pts = c.rel.map(function (p) { return [g.ix + p[0], g.iy + p[1]]; });
    window.SCDM_FORME = c;
    return c;
  }

  /* =====================================================================
     7. LE BRANCHEMENT
     ===================================================================== */

  function poser(c, pts) {
    c.moveTo(pts[0][0], pts[0][1]);
    for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
    c.closePath();
  }

  function brancher() {
    if (!ctx || ellipseOrig) return;
    ellipseOrig = ctx.ellipse.bind(ctx);
    ctx.ellipse = function (cx, cy, rx, ry, rot, a0, a1, sens) {
      var S = window.SCDM;
      if (S && S.etat && S.etat.forme === 'contour' && S.etat.img) {
        var t = calculer();
        if (t && t.pts.length > 5) { poser(ctx, t.pts); return; }
      }
      return ellipseOrig(cx, cy, rx, ry, rot, a0, a1, sens);
    };
  }

  /* =====================================================================
     8. CE QU ON DIT AU CLIENT
        Un client qui choisit « a la forme » et recoit un rectangle appelle.
        Mieux vaut l ecrire pendant qu il configure.
     ===================================================================== */

  var MOTS = {
    alpha: 'Découpe à la forme : le contour suit exactement votre dessin — votre fichier a un fond transparent.',
    fond:  'Découpe à la forme : le fond uni de votre image a été détecté et retiré, le contour suit le dessin. Vérifiez le trait magenta avant d’approuver.',
    plein: 'Ce fichier n’a pas de fond transparent et son fond n’est pas d’une seule couleur : la découpe suivra le rectangle de l’image. Pour une vraie découpe à la forme, envoyez un PNG détouré, un PDF ou un fichier .AI.'
  };

  function poserNote() {
    if (note) return note;
    var cotes = document.getElementById('scdmCotes');
    if (!cotes || !cotes.parentElement) return null;
    note = document.createElement('p');
    note.className = 'scdm-forme-note';
    note.hidden = true;
    note.style.cssText = 'margin:6px 0 0;font-size:11.5px;line-height:1.45;color:var(--slate,#98A1B2)';
    var apres = cotes.parentElement.querySelector('.scdm-coupe-note') || cotes;
    apres.parentElement.insertBefore(note, apres.nextSibling);
    return note;
  }

  function rafraichirNote() {
    var n = poserNote();
    if (!n) return;
    var S = window.SCDM;
    if (!S || !S.etat || !S.etat.img || S.etat.forme !== 'contour') { n.hidden = true; return; }
    var mode = (analyse && analyse.mode) || 'plein';
    n.textContent = MOTS[mode] || MOTS.plein;
    n.style.color = mode === 'plein' ? '#B45309' : 'var(--slate,#98A1B2)';
    n.hidden = false;
  }

  /* =====================================================================
     9. DEMARRAGE
        On surveille l image courante : quand elle change, on refait l analyse
        et on vide le cache. SCDM.etat.img est remplacee par scdm.js a chaque
        nouveau fichier ou nouveau modele, il n y a pas d evenement a ecouter.
     ===================================================================== */

  function veiller() {
    var S = window.SCDM;
    if (!S || !S.etat) return;
    var im = S.etat.img;
    if (im !== derniere) {
      derniere = im;
      cache = null;
      analyse = im ? analyser(im) : null;
      window.SCDM_FORME = null;
      rafraichirNote();
      /* L analyse peut arriver apres le premier trace : on redemande un
         rendu, sans quoi le cercle resterait jusqu au prochain clic. */
      if (im && S.etat.forme === 'contour') redessiner();
    }
    rafraichirNote();
  }

  /* scdm.js ne publie pas peindreApercu(). Le geste le plus leger pour lui
     faire refaire un rendu sans rien changer a l etat : reposer la valeur du
     curseur de contour, qui declenche son ecouteur « input ». */
  function redessiner() {
    var b = document.getElementById('scdmBordure');
    if (!b) return;
    b.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function demarrer() {
    cv = document.getElementById('scdmCanvas');
    if (!cv) return false;
    ctx = cv.getContext('2d');
    brancher();
    poserNote();
    veiller();
    setInterval(veiller, 250);
    document.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-champ="forme"]') : null;
      if (b) setTimeout(rafraichirNote, 30);
    });
    return true;
  }

  function quandPret(n) {
    if (document.getElementById('scdmCanvas') && window.SCDM) return demarrer();
    if ((n || 0) > 80) return;
    setTimeout(function () { quandPret((n || 0) + 1); }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { quandPret(0); });
  } else {
    quandPret(0);
  }

  /* =====================================================================
     10. CE QU ON PRETE AU PDF DE DECOUPE
        scdm-cutcontour.js doit produire un trace IDENTIQUE a celui que le
        client a approuve a l ecran, mais assez fin pour une lame. Il refait
        donc le calcul avec les MEMES fonctions, a la resolution d impression,
        au lieu d en ecrire une deuxieme version qui deriverait avec le temps.
     ===================================================================== */
  window.SCDM_DECOUPE = {
    analyser: analyser,
    tracer: tracerDe
  };
})();
